// command-adapter.ts — registry of the 11 M0 command adapters (spec §9.1;
// team-ai-pack workflows/autopilot/command-adapters.md is the source table
// this registry must match 1:1 — command-adapter.test.ts cross-checks IDs
// against a parse of that markdown table).
//
// `allowed_tools` is the ONE source of truth for `--allowedTools` (P2 argv)
// AND `binding.allowed_tools` (P1 writer) per the pack doc's own banner:
// "một nguồn, ba nơi tiêu thụ". This module only VALIDATES rule syntax; it
// does not hardcode business logic (§2/§9.1: "CLI không hardcode nghiệp vụ").
//
// Two hard validation rules enforced at registration time (both reject, no
// silent coercion):
//   B27a — no shorthand: every rule is `Name` or `Name(spec)`; MCP rules
//     must be `mcp__<server>__<tool>` (a bare `mcp__<server>` is rejected).
//   B29  — Edit-family all-or-nothing: declaring ANY of {Edit, Write,
//     MultiEdit, NotebookEdit} requires declaring ALL FOUR.
//
// Adapter DATA rows live in command-adapter-table.ts (split out to keep both
// files under the 200-line project limit).
import { z } from "zod";
import {
  type CommandAdapterDefinition,
  type CommandAdapterExecutor,
  type CommandAdapterKind,
  EDIT_FAMILY,
  RAW_ADAPTERS,
} from "./command-adapter-table.js";

export type {
  CommandAdapterDefinition,
  CommandAdapterExecutor,
  CommandAdapterKind,
} from "./command-adapter-table.js";
export { EDIT_FAMILY } from "./command-adapter-table.js";

export const COMMAND_ADAPTER_KINDS = [
  "survey",
  "plan",
  "testcase",
  "implement",
  "test",
  "fix",
  "review",
  "decision",
  "revert",
  "deploy",
] as const;

// `Name` or `Name(spec)` — no bare `mcp__<server>` (B27a): an MCP rule must
// spell `mcp__<server>__<tool>` in full, checked as a second pass below
// because the generic rule-shape regex alone can't distinguish "valid
// non-MCP rule" from "MCP rule missing its tool segment".
//
// IMPORTANT: the "bare" pattern must match ONLY `mcp__<server>` (exactly one
// `__`-delimited segment after the prefix) — a naive `[A-Za-z0-9_]+` charset
// also matches the SECOND `__` inside a fully-qualified
// `mcp__<server>__<tool>` rule (underscore is in the charset), which would
// wrongly flag a valid rule as bare. The pattern below requires the segment
// after `mcp__` to contain no further `__`, so `mcp__gitnexus` matches
// (bare, reject) but `mcp__gitnexus__query` does not (qualified, accept).
const RULE_SHAPE_RE = /^[A-Za-z][A-Za-z0-9_]*(\([^()]*\))?$/;
const BARE_MCP_SERVER_RE = /^mcp__(?:(?!__)[A-Za-z0-9_])+$/;

function validateRuleSyntax(id: string, rules: string[]): void {
  for (const rule of rules) {
    if (BARE_MCP_SERVER_RE.test(rule)) {
      throw new Error(
        `command-adapter registry: "${id}" khai rule MCP shorthand trần "${rule}" ` +
          '(B27a) — phải ghi đủ "mcp__<server>__<tool>".',
      );
    }
    if (!RULE_SHAPE_RE.test(rule)) {
      throw new Error(
        `command-adapter registry: "${id}" khai rule không hợp lệ "${rule}" — ` +
          'phải dạng "Name" hoặc "Name(spec)".',
      );
    }
  }
}

function validateEditFamilyAllOrNothing(id: string, rules: string[]): void {
  const present = EDIT_FAMILY.filter((name) => rules.includes(name));
  if (present.length > 0 && present.length < EDIT_FAMILY.length) {
    const missing = EDIT_FAMILY.filter((name) => !rules.includes(name));
    throw new Error(
      `command-adapter registry: "${id}" khai Edit-family thiếu ${missing.join(", ")} ` +
        `(B29) — có "${present.join(", ")}" thì phải có đủ cả 4.`,
    );
  }
}

function validateExecutorMatchesKind(
  id: string,
  kind: CommandAdapterKind,
  executor: CommandAdapterExecutor,
): void {
  const mustBeController = kind === "revert" || kind === "deploy";
  if (mustBeController && executor !== "controller") {
    throw new Error(
      `command-adapter registry: "${id}" kind="${kind}" phải executor="controller" ` +
        `(§5.1: "revert/deploy do controller thực thi recipe đã duyệt"), nhận "${executor}".`,
    );
  }
  if (!mustBeController && executor !== "claude") {
    throw new Error(
      `command-adapter registry: "${id}" kind="${kind}" phải executor="claude", nhận "${executor}".`,
    );
  }
}

// Validate + freeze at module load so an invalid table fails immediately
// (fail fast — a bad registry must never silently ship).
for (const adapter of RAW_ADAPTERS) {
  validateRuleSyntax(adapter.id, adapter.allowedTools);
  validateEditFamilyAllOrNothing(adapter.id, adapter.allowedTools);
  validateExecutorMatchesKind(adapter.id, adapter.kind, adapter.executor);
}

export const COMMAND_ADAPTERS: ReadonlyMap<string, Readonly<CommandAdapterDefinition>> = new Map(
  RAW_ADAPTERS.map((a) => [a.id, Object.freeze({ ...a, allowedTools: [...a.allowedTools] })]),
);

export const COMMAND_ADAPTER_IDS: readonly string[] = RAW_ADAPTERS.map((a) => a.id).sort();

/** Registry lookup: throws on unknown ID — no fallback to guessing a slash command. */
export function resolveCommandAdapter(id: string): CommandAdapterDefinition {
  const adapter = COMMAND_ADAPTERS.get(id);
  if (adapter === undefined) {
    throw new Error(
      `command-adapter registry: "${id}" chưa khai trong registry — không fallback đoán slash command.`,
    );
  }
  return adapter;
}

/**
 * Non-functional requirement: "subagent ≤ max_internal_subagents". Resolves
 * the adapter AND checks its declared subagent count against the caller's
 * effective-config limit — reject rather than silently clamp, so a
 * mismatched adapter table entry is caught instead of quietly under- or
 * over-provisioning a session.
 */
export function resolveCommandAdapterWithSubagentCap(
  id: string,
  maxInternalSubagentsFromConfig: number,
): CommandAdapterDefinition {
  const adapter = resolveCommandAdapter(id);
  if (adapter.maxInternalSubagents > maxInternalSubagentsFromConfig) {
    throw new Error(
      `command-adapter registry: "${id}" cần ${adapter.maxInternalSubagents} subagent, ` +
        `vượt max_internal_subagents=${maxInternalSubagentsFromConfig} của config hiện hành.`,
    );
  }
  return adapter;
}

export const CommandAdapterIdSchema = z.enum(
  RAW_ADAPTERS.map((a) => a.id) as [string, ...string[]],
);
